import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
  .bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -1;
  }

  :host {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 1024px;
    height: 100%;
    margin: 0 auto;
    font-family: "Onesize", serif;
    color: #2f1e0e;
    padding: 1rem;
    box-sizing: border-box;
  }

  .title {
    font-size: 2rem;
    font-weight: bold;
    text-align: center;
    margin-bottom: 100px;
    text-shadow: 1px 1px 2px #fff8e1;
  }

  #con {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    flex: 1;
    margin-bottom: 1rem;
  }

  .header,
  .content {
    flex: 1;
    border: 2px solid #7c5a34;
    border-radius: 8px;
    background-color: rgba(255, 255, 255, 0.6);
    padding: 1rem;
    box-sizing: border-box;
    overflow-y: auto;
    max-height: 500px;
  }

  .header img {
    width: 100%;
    height: auto;
    object-fit: contain;
    border-radius: 8px;
  }
    

  .content p {
    margin: 0.5rem 0;
    line-height: 1.5;
  }

  .footer {
    border: 2px solid #7c5a34;
    border-radius: 8px;
    padding: 1rem;
    background-color: rgba(255, 255, 255, 0.6);
  }

  .buttons {
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: space-between;
  }

  .button {
    font-family: "Onesize", serif;
    margin: 5px;
    background-color: #d9c2a3;
    border: 1px solid #6b4c2e;
    border-radius: 4px;
    padding: 0.5rem 1rem;
    text-decoration: none;
    color: #2f1e0e;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-block;
  }

  .button:hover {
    background-color: #c4a97c;
  }

  .button.active {
    background-color: #a57b58;
    color: #fff;
    border-color: #5c3c1b;
  }

  #open-characterProfile { 
       font-size: 20px;
  }

  #open-miniGame {
    font-size: 20px;
  }

  #open-map {
        margin-left: 0px;
    }
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
        void this.refreshGameState();
        this.shadowRoot?.addEventListener("click", event => {
            if ((event.target as HTMLElement).id === "open-map") {
                this._gameEventService.switchPage(Page.Map);
            }
        });
        this.shadowRoot?.addEventListener("click", event => {
            if ((event.target as HTMLElement).id === "open-register") {
                this._gameEventService.switchPage(Page.Register);
            }
        });

        this.shadowRoot?.addEventListener("click", event => {
            if ((event.target as HTMLElement).id === "open-login") {
                this._gameEventService.switchPage(Page.Login);
            }
        });

        void this.refreshGameState();
        this.shadowRoot?.addEventListener("click", event => {
            if ((event.target as HTMLElement).id === "open-characterProfile") {
                this._gameEventService.switchPage(Page.CharacterProfile);
            }
        });
        void this.refreshGameState();
        this.shadowRoot?.addEventListener("click", event => {
            if ((event.target as HTMLElement).id === "open-miniGame") {
                this._gameEventService.switchPage(Page.MiniGame);
            }
        });

        this.shadowRoot?.addEventListener("click", event => {
            const target: HTMLElement = event.target as HTMLElement;
            console.log("Button clicked:", target.id); // Log de ID van de geklikte knop

            if (target.id === "open-login") {
                console.log("Login knop geklikt");
                this._gameEventService.switchPage(Page.Login);
            }

            if (target.id === "open-register") {
                this._gameEventService.switchPage(Page.Register);
            }

            if (target.id === "logout") {
                console.log("[Logout] Gebruiker wordt uitgelogd...");

                localStorage.removeItem("sessionId");
                localStorage.removeItem("user");
                localStorage.removeItem("playerSessionId");

                window.dispatchEvent(new CustomEvent("sessionUpdated"));
                this._gameEventService.switchPage(Page.Canvas);
            }
        });
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>
            <img src="/assets/img/rooms/background.png" class="bg">
            ${this.renderTitle()}
            ${this.renderLogging()} <!-- Rechtsboven de login/register knoppen -->

            <div id="con">
                ${this.renderHeader()}
                ${this.renderContent()}
            </div>
            <button class="button" id="open-characterProfile">Character Profile</button>
            <button class="button" id="open-miniGame">Mini Game</button>
            ${this.renderFooter()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    private renderLogging(): string {
        const isLoggedIn: boolean = localStorage.getItem("playerSessionId") !== null && localStorage.getItem("user") !== null;

        console.log("Render logging, isLoggedIn:", isLoggedIn);
        console.log("sessionId in localStorage:", localStorage.getItem("playerSessionId"));

        return `
            <div style="position: absolute; top: 10px; right: 10px;">
                ${!isLoggedIn
                    ? `
                        <button class="button" id="open-login">Login</button>
                        <button class="button" id="open-register">Register</button>
                    `
                    : `
                        <button class="button" id="logout">Logout</button>
                    `}
            </div>
        `;
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            return `
            <div class="header">
                <img src="/assets/img/rooms/${roomImages[0]}.png" />
            </div>
        `;
        }

        return "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
            <div class="content">
                ${this._currentGameState?.text.map(text => `<p>${text}</p>`).join("") || ""}
            </div>
        `;
    }

    //           <button class="button" id="open-inventory">Inventory</button>
    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        return html`
            <div class="footer">
                <div class="buttons">
                    <div>
                        ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                        <a class="button" id="open-map">Map</a>
                    </div>
                    <div>
                        ${this._selectedActionButton
                            ? this._currentGameState?.objects.map(button => this.renderGameObjectButton(button)) || ""
                            : ""
                        }
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render();

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );
        console.log(state);
        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state
        this.updateGameState(state);
    }
}
